import { Inject } from '@odinjs/odin';
import { FilterAPI, MediaContainerAPI, MediaContainerData, PanelAPI, PDFViewerAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnBeforeClose, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { PrescriptionReportProps } from '../../../interfaces/pep/prescription-report-props';
import PrescriptionReportService from '../../../services/pep/prescription-report-service';
import { DateFilterUtils } from '../../../utils/date-filter-utils';
import BaseController from '../../BaseController';
import { BrowserService } from '../../commons/browser/browser-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import ReportService from '../../../services/report-service';
import { PrescriptionReportAliases } from '../../../codegen';

interface PrescriptionReportFilter {
  finalDate: Date;
  initialDate: Date;
}

@Controller({ component: 'prescription-report-layout', fragment: 'prescription-report' })
class PrescriptionReportController extends BaseController {

  @Inject(PrescriptionReportAliases.PRESCRIPTION_REPORT_PDF_VIEWER)
  private pdfViewerApi!: PDFViewerAPI;

  @Inject(PrescriptionReportAliases.PRESCRIPTION_REPORT_FILTER)
  private filterApi!: FilterAPI;

  @Inject(PrescriptionReportAliases.PRESCRIPTION_REPORT_PANEL_NO_RESULT)
  private panelNoResultApi!: PanelAPI;

  @Inject(PrescriptionReportAliases.PRESCRIPTION_REPORT_PANEL_PDF)
  private panelPdfApi!: PanelAPI;

  @Inject(PrescriptionReportAliases.PANEL_LOADER)
  private panelLoaderApi!: PanelAPI;

  @Inject(PrescriptionReportAliases.MEDIA_CONTAINER)
  private mediaContainerApi!: MediaContainerAPI;

  @OnMount(PrescriptionReportAliases.PRESCRIPTION_REPORT_PDF_VIEWER)
  handleMountPdfViewer(): void {
    const file = this.context.get('reportFile')

    if (!file) {
      this.panelPdfApi.setVisible(false);
      return;
    }
    this.panelLoaderApi.setVisible(false)
    this.pdfViewerApi.setVisible(true);
    this.pdfViewerApi.setSrc(file);

    this.pdfViewerApi.setDownloadPdfExpression('tws.view-report');
    this.pdfViewerApi.setOpenPdfExpression('text.open-report');
  }

  @OnMount(PrescriptionReportAliases.PRESCRIPTION_REPORT_LAYOUT)
  onMount(): void {

    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      browser: BrowserService.detectBrowser(),
      encounterId,
      patientId
    });

  }

  @OnMount(PrescriptionReportAliases.PRESCRIPTION_REPORT_FILTER)
  onMountFilter(): void {

    this.enabledPanelNoResult();

    const initialParameters = {
      finalDate: DateFilterUtils.getCurrentDate(),
      initialDate: DateFilterUtils.anyLessDate(),
    };

    this.filterApi.setData(initialParameters);

  }

  @OnSubmit(PrescriptionReportAliases.PRESCRIPTION_REPORT_FILTER)
  onSubmitFilter(): void {
    this.enabledPanelWithLoading();

    const { finalDate, initialDate } = this.filterApi.getRecord() as unknown as PrescriptionReportFilter;

    const parametersReport = {
      encounterId: this.context.get('encounterId') as number,
      finalDate,
      initialDate
    } as PrescriptionReportProps;

    if (this.context.get('browser') === 'Safari') {
      this.loadReportImagens(parametersReport);
    } else {
      this.loadReport(parametersReport);
    }
  }

  @OnBeforeClose(PrescriptionReportAliases.MEDIA_CONTAINER)
  onBeforeMediaContainerClose(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  loadReportImagens(parametersReport: PrescriptionReportProps): void {
    const prescriptionReportService = new PrescriptionReportService();
    const reportService = new ReportService(this);

    prescriptionReportService.generateReportImages(parametersReport).then(response => {
      const imagePaths = response.body as string[];
      const reportData = imagePaths.map(reportService.getReportImage);
      return Promise.all(reportData);
    }).then(mediaContainerData => {
      this.mediaContainerApi.setData(mediaContainerData as unknown as MediaContainerData[]);
      this.mediaContainerApi.open();
      this.panelLoaderApi.setVisible(false);
    }).catch(() => {
      this.enabledPanelNoResult();
      this.errorReport();
    })
  }

  loadReport(parametersReport: PrescriptionReportProps): void {
    const prescriptionReportService = new PrescriptionReportService();
    prescriptionReportService.generateReport(parametersReport).then(reportFile => {
      this.context.set({ reportFile })
    }).catch(() => {
      this.enabledPanelNoResult();
      this.errorReport();
    }).finally(() => {
      this.panelLoaderApi.setVisible(false)
      this.panelPdfApi.setVisible(true)
    });
  }

  enabledPanelNoResult(): void {
    this.panelLoaderApi.setVisible(false);
    this.panelPdfApi.setVisible(false);
    this.panelNoResultApi.setVisible(true);
  }

  enabledPanelWithLoading(): void {
    this.panelLoaderApi.setVisible(true);
    this.panelPdfApi.setVisible(false);
    this.panelNoResultApi.setVisible(false);
  }

  errorReport(): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'prescription-report-toast',
      level: ToastLevel.ERROR,
      message: MessageService.translate('tws.error-generate-report'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: MessageService.translate('tws.error-generic'),
      type: ToastType.BANNER,
    });
  }

}

export default PrescriptionReportController;